import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import Baseball from '@/components/Baseball';
import RadioTower from '@/components/RadioTower';
import {
  RetroBackground,
  RetroButton,
  RetroPageTitle,
  PALETTE,
  ACCENTS,
  DESIGN_TOKENS
} from '@/components/RetroUI';

// Section component - framed panel with header band (reference style)
const Section = ({ title, children, variant = 'default' }) => (
  <div 
    className="mb-6"
    style={{
      background: PALETTE.cream,
      border: `4px solid ${PALETTE.ink}`,
      borderRadius: DESIGN_TOKENS.borderRadius.lg,
      boxShadow: `5px 5px 0px rgba(0,0,0,0.3)`,
      overflow: 'hidden',
    }}
  >
    <div 
      className="text-center py-2"
      style={{
        fontFamily: DESIGN_TOKENS.fonts.display,
        fontSize: '11px',
        color: variant === 'highlight' ? PALETTE.ink : PALETTE.cream,
        background: variant === 'highlight' ? ACCENTS.gold : PALETTE.grass,
        borderBottom: `3px solid ${PALETTE.ink}`,
        letterSpacing: '1px',
      }}
    >
      {title}
    </div>
    <div className="p-4">
      {children}
    </div>
  </div>
);

// ScoreRow component - moved outside to prevent re-renders
const ScoreRow = ({ score, result, bgColor, borderColor }) => (
  <div 
    className="flex items-center gap-4 p-3"
    style={{
      background: bgColor,
      borderLeft: `4px solid ${borderColor}`,
      borderRadius: DESIGN_TOKENS.borderRadius.sm,
    }}
  >
    <span style={{
      fontFamily: DESIGN_TOKENS.fonts.display,
      fontSize: '14px',
      color: borderColor,
      width: '40px',
    }}>
      {score}
    </span>
    <span style={{
      fontFamily: DESIGN_TOKENS.fonts.ui,
      fontWeight: 600,
      fontSize: '13px',
      color: PALETTE.ink,
    }}>
      = {result}
    </span>
  </div>
);

export default function HowToPlay() {
  const navigate = useNavigate();

  // Mark that user has read the How to Play page
  useEffect(() => {
    localStorage.setItem('hasReadHowToPlay', 'true');
  }, []);

  return (
    <RetroBackground variant="tanStripes" className="p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <RetroButton
            variant="tertiary"
            size="sm"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ChevronLeft size={20} />
          </RetroButton>
          <div className="flex-1">
            <RetroPageTitle color="ink" size="md">
              HOW TO PLAY
            </RetroPageTitle>
          </div>
          <div style={{ width: '52px' }} />
        </div>

        {/* Icons - Radio Tower and Baseball */}
        <div className="flex justify-center items-center gap-4 mb-8">
          <RadioTower size={50} animated={true} />
          <Baseball size={60} />
        </div>

        {/* Overview */}
        <Section title="THE BASICS">
          <p style={{
            fontFamily: DESIGN_TOKENS.fonts.ui,
            fontSize: '10px',
            color: PALETTE.ink,
            lineHeight: 1.8,
          }}>
            Radio Baseball is an in person <strong>MULTIPLAYER</strong> music trivia game played like baseball. 
            Listen to 30-second song clips and guess the <strong>Title</strong>, <strong>Artist</strong>, <strong>Album</strong>, and <strong>Year</strong>. 
            The more you get right, the better your hit!
          </p>
        </Section>

        {/* Scoring */}
        <Section title="SCORING">
          <div className="space-y-2">
            <ScoreRow score="0" result="OUT" bgColor="#FEE2E2" borderColor="#EF4444" />
            <ScoreRow score="1" result="SINGLE (1st base)" bgColor={PALETTE.cream} borderColor={PALETTE.dirt} />
            <ScoreRow score="2" result="DOUBLE (2nd base)" bgColor={PALETTE.cream} borderColor={PALETTE.dirt} />
            <ScoreRow score="3" result="TRIPLE (3rd base)" bgColor={PALETTE.cream} borderColor={PALETTE.dirt} />
            <ScoreRow score="4" result="HOME RUN!" bgColor="#DCFCE7" borderColor={PALETTE.grass} />
          </div>
        </Section>

        {/* How a Turn Works */}
        <Section title="HOW A TURN WORKS">
          <ol className="space-y-3">
            {[
              { text: <>The <strong>pitcher</strong> holds the device and presses &quot;PITCH!&quot;</> },
              { text: 'A 30-second song clip plays for everyone to hear' },
              { text: <><strong>Only the batter</strong> shouts out their guesses (Title, Artist, Album, Year) - no help from teammates!</> },
              { text: 'After 30 seconds, the pitcher sees the answers and taps which ones were correct' },
              { text: 'The result is recorded and runners advance!' },
            ].map((item, index) => (
              <li key={index} className="flex gap-3">
                <span style={{
                  fontFamily: DESIGN_TOKENS.fonts.display,
                  fontSize: '11px',
                  color: PALETTE.dirt,
                }}>
                  {index + 1}.
                </span>
                <span style={{
                  fontFamily: DESIGN_TOKENS.fonts.ui,
                  fontSize: '13px',
                  color: PALETTE.ink,
                  lineHeight: 1.6,
                }}>
                  {item.text}
                </span>
              </li>
            ))}
          </ol>
        </Section>

        {/* Game Flow */}
        <Section title="GAME FLOW">
          <ul className="space-y-2">
            {[
              'Teams alternate batting and pitching each half-inning',
              '3 outs ends your team\'s turn at bat',
              'Runners score when they reach home plate',
              'Choose 3, 5, 7, or 9 innings per game',
              'Most runs at the end wins!',
            ].map((item, index) => (
              <li 
                key={index}
                style={{
                  fontFamily: DESIGN_TOKENS.fonts.ui,
                  fontSize: '13px',
                  color: PALETTE.ink,
                  paddingLeft: '12px',
                  position: 'relative',
                }}
              >
                <span style={{
                  position: 'absolute',
                  left: 0,
                  color: PALETTE.grass,
                }}>•</span>
                {item}
              </li>
            ))}
          </ul>
        </Section>

        {/* Tips */}
        <Section title="TIPS">
          <ul className="space-y-2">
            {[
              <>Release year only counts if it&apos;s <strong>exact</strong></>,
              'Batters guess out loud - no typing!',
              'Pick from six curated playlists spanning every era and genre',
            ].map((item, index) => (
              <li 
                key={index}
                style={{
                  fontFamily: DESIGN_TOKENS.fonts.ui,
                  fontSize: '13px',
                  color: PALETTE.ink,
                  paddingLeft: '12px',
                  position: 'relative',
                }}
              >
                <span style={{
                  position: 'absolute',
                  left: 0,
                  color: PALETTE.grass,
                }}>•</span>
                {item}
              </li>
            ))}
          </ul>
        </Section>

        {/* Pro Tip */}
        <Section title="PRO TIP" variant="highlight">
          <p style={{
            fontFamily: DESIGN_TOKENS.fonts.ui,
            fontSize: '13px',
            color: PALETTE.ink,
            lineHeight: 1.6,
          }}>
            Pair with a bluetooth speaker before starting your game for a more immersive experience!
          </p>
        </Section>

        {/* Ready to Play */}
        <div className="text-center mt-8">
          <RetroButton
            variant="primary"
            size="lg"
            onClick={() => navigate('/dashboard')}
            data-testid="start-playing-btn"
          >
            START PLAYING
          </RetroButton>
        </div>
      </div>
    </RetroBackground>
  );
}
